import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import { ChooseSoundworldModalSimple } from '../../components/catalogue/ChooseSoundworldModalSimple';
import { CatalogueMainHeader } from '../../components/catalogue/CatalogueMainHeader';
import { AudioUploadSlot } from '../../components/catalogue/AudioUploadSlot';
import { ProjectForm, FileUploadArea } from '../../components/catalogue';
import { useProjectCreation } from '../../hooks/useProjectCreation';
import { useSoundworlds } from '../../hooks/useSoundworlds';

export const TransferGenrePage = ({ username }) => {
    const { isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [audioStems, setAudioStems] = useState({
        vocalsFile: null,
        bassFile: null,
        midsFile: null,
        drumsFile: null,
    });

    const {
        fileUploadState,
        setFileUploadState,
        formData,
        setFormData,
        showError,
        isValidAudioFile,
        createProject,
        uploadFile,
        deleteFile,
        executeProject,
        generateComposition,
        renderAudio,
        renderStems,
    } = useProjectCreation();

    const { soundworlds } = useSoundworlds();

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    // Get the pretty name for the selected soundworld
    const selectedSoundworld = soundworlds.find(sw => sw.name === formData.soundworld);
    const selectedSoundworldPretty = selectedSoundworld?.name_pretty;

    const uploadStemToProject = async (file, role) => {
        if (!fileUploadState.projectId || !file) return;

        try {
            await uploadFile(file, fileUploadState.projectId, role);
        } catch (err) {
            showError(`Failed to upload ${role} stem.`);
        }
    };

    useEffect(() => {
        const stems = [
            [audioStems.vocalsFile, 'LEAD'],
            [audioStems.bassFile, 'BASS'],
            [audioStems.midsFile, 'CHORDS'],
            [audioStems.drumsFile, 'DRUMS']
        ];
        stems.forEach(([file, role]) => {
            if (file) uploadStemToProject(file, role);
        });
    }, [audioStems.vocalsFile, audioStems.bassFile, audioStems.midsFile, audioStems.drumsFile, fileUploadState.projectId]);

    const handleFileUploadAuto = async (file) => {
        if (!file || !formData.projectName.trim()) {
            showError("Please complete all required fields before uploading.");
            return;
        }

        setFileUploadState(prev => ({ ...prev, selectedFile: file, loading: true }));

        try {
            const projectId = await createProject('derivative', formData.loopMode ? 'loop' : 'normal');
            if (projectId) {
                setFileUploadState(prev => ({ ...prev, projectId }));
                await uploadFile(file, projectId, 'input_audio');
            }
        } catch (err) {
            showError('Something went wrong during upload.');
        } finally {
            setFileUploadState(prev => ({ ...prev, loading: false }));
        }
    };

    const handleFileSelect = (file) => {
        if (isValidAudioFile(file)) {
            handleFileUploadAuto(file);
        } else {
            showError("Please upload a valid audio file (MP3, WAV, etc.).");
        }
    };

    const handleDrop = (file) => {
        if (isValidAudioFile(file)) {
            handleFileUploadAuto(file);
        } else {
            showError("Please upload a valid audio file (MP3, WAV, etc.).");
        }
    };

    const handleGenerate = async () => {
        if (!fileUploadState.projectId) return;

        const soundworldId = formData.soundworld || 'ChillPianoAging';

        await executeProject(
            fileUploadState.projectId,
            soundworldId,
            formData.loopMode,
            formData.sectionType,
            () => {
                navigate(`/catalogue/${fileUploadState.projectId}`);
            }
        );
    };

    const handleFormDataChange = (data) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    const handleAcknowledgedChange = (acknowledged) => {
        setFileUploadState(prev => ({ ...prev, acknowledged }));
    };

    const handleStemChange = (stemType, file) => {
        setAudioStems(prev => ({ ...prev, [stemType]: file }));
    };

    const handleSoundworldSelect = (soundworldName) => {
        setFormData(prev => ({ ...prev, soundworld: soundworldName }));
        setShowModal(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="h-screen overflow-hidden bg-background">
            <CatalogueMainHeader username={ username } backButton />

            <div className="h-full overflow-y-auto px-6 py-6">
                <div className="flex flex-col items-center justify-center min-h-full">
                    <h1 className="text-3xl font-semibold text-center text-card-foreground">
                        Fast Track
                    </h1>
                    <p className="text-sm text-muted-foreground mt-2 text-center max-w-md">
                        Drop a loop or your work-in-progress track and click Generate. The soundworld will generate additional instrumental parts for your track.
                    </p>

                    <ProjectForm
                        formData={ formData }
                        onFormDataChange={ handleFormDataChange }
                        onSoundworldSelect={ () => setShowModal(true) }
                        selectedSoundworldPretty={ selectedSoundworldPretty }
                    />

                    <div className="flex flex-wrap gap-4 mt-6 justify-center">
                        <AudioUploadSlot
                            label="Vocals"
                            file={ audioStems.vocalsFile }
                            setFile={ (file) => handleStemChange('vocalsFile', file) }
                            disabled={ !fileUploadState.projectId }
                        />
                        <AudioUploadSlot
                            label="Bass"
                            file={ audioStems.bassFile }
                            setFile={ (file) => handleStemChange('bassFile', file) }
                            disabled={ !fileUploadState.projectId }
                        />
                        <AudioUploadSlot
                            label="Mids"
                            file={ audioStems.midsFile }
                            setFile={ (file) => handleStemChange('midsFile', file) }
                            disabled={ !fileUploadState.projectId }
                        />
                        <AudioUploadSlot
                            label="Drums"
                            file={ audioStems.drumsFile }
                            setFile={ (file) => handleStemChange('drumsFile', file) }
                            disabled={ !fileUploadState.projectId }
                        />
                    </div>

                    <FileUploadArea
                        fileUploadState={ fileUploadState }
                        onFileSelect={ handleFileSelect }
                        onDrop={ handleDrop }
                        onGenerate={ handleGenerate }
                        onAcknowledgedChange={ handleAcknowledgedChange }
                        acceptAudio={ true }
                        buttonText="Generate"
                    />
                </div>
            </div>

            { showModal && (
                <ChooseSoundworldModalSimple
                    isOpen={ showModal }
                    onClose={ () => setShowModal(false) }
                    onSelect={ handleSoundworldSelect }
                />
            ) }
        </div>
    );
};